/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { Exporter } from './Exporter';
import { LazilyTransformingAstService } from './LazilyTransformingAstService';
import { CellContentParser } from './CellContentParser';
import { buildColumnSearchStrategy } from './Lookup/SearchStrategy';
import { Config } from './Config';
import { DateTimeHelper } from './DateTimeHelper';
import { CrudOperations } from './CrudOperations';
import { DependencyGraph } from './DependencyGraph';
import { Evaluator } from './Evaluator';
import { GraphBuilder } from './GraphBuilder';
import { UIElement } from './i18n';
import { NamedExpressions } from './NamedExpressions';
import { NumberLiteralHelper } from './NumberLiteralHelper';
import { buildLexerConfig, ParserWithCaching, Unparser } from './parser';
import { Serialization } from './Serialization';
import { EmptyStatistics, Statistics, StatType } from './statistics';
import { SheetSizeLimitExceededError } from './errors';
import { findBoundaries, validateAsSheet } from './Sheet';
import { FunctionRegistry } from './interpreter/FunctionRegistry';
export class BuildEngineFactory {
    static buildEngine(config, sheets = {}, stats = config.useStats ? new Statistics() : new EmptyStatistics()) {
        stats.start(StatType.BUILD_ENGINE_TOTAL);
        const namedExpressions = new NamedExpressions();
        const functionRegistry = new FunctionRegistry(config);
        const lazilyTransformingAstService = new LazilyTransformingAstService(stats);
        const dependencyGraph = DependencyGraph.buildEmpty(lazilyTransformingAstService, config, functionRegistry, namedExpressions, stats);
        const columnSearch = buildColumnSearchStrategy(dependencyGraph, config, stats);
        const sheetMapping = dependencyGraph.sheetMapping;
        const addressMapping = dependencyGraph.addressMapping;
        for (const sheetName in sheets) {
            if (Object.prototype.hasOwnProperty.call(sheets, sheetName)) {
                const sheet = sheets[sheetName];
                validateAsSheet(sheet);
                const boundaries = findBoundaries(sheet);
                if (boundaries.height > config.maxRows || boundaries.width > config.maxColumns) {
                    throw new SheetSizeLimitExceededError();
                }
                const sheetId = sheetMapping.addSheet(sheetName);
                addressMapping.autoAddSheet(sheetId, sheet, boundaries);
            }
        }
        const parser = new ParserWithCaching(config, functionRegistry, sheetMapping.get);
        const unparser = new Unparser(config, buildLexerConfig(config), sheetMapping.fetchDisplayName, namedExpressions);
        const dateHelper = new DateTimeHelper(config);
        const numberLiteralHelper = new NumberLiteralHelper(config);
        const cellContentParser = new CellContentParser(config, dateHelper, numberLiteralHelper);
        const crudOperations = new CrudOperations(config, stats, dependencyGraph, columnSearch, parser, cellContentParser, lazilyTransformingAstService, namedExpressions);
        stats.measure(StatType.GRAPH_BUILD, () => {
            const graphBuilder = new GraphBuilder(dependencyGraph, columnSearch, parser, cellContentParser, config, stats);
            graphBuilder.buildGraph(sheets);
        });
        lazilyTransformingAstService.undoRedo = crudOperations.undoRedo;
        lazilyTransformingAstService.parser = parser;
        const exporter = new Exporter(config, namedExpressions, sheetMapping.fetchDisplayName);
        const serialization = new Serialization(dependencyGraph, unparser, config, exporter);
        const evaluator = new Evaluator(dependencyGraph, columnSearch, config, stats, dateHelper, numberLiteralHelper, functionRegistry, namedExpressions, serialization);
        evaluator.run();
        stats.end(StatType.BUILD_ENGINE_TOTAL);
        return {
            config,
            stats,
            dependencyGraph,
            columnSearch,
            parser,
            unparser,
            cellContentParser,
            evaluator,
            lazilyTransformingAstService,
            crudOperations,
            exporter,
            namedExpressions,
            serialization,
            functionRegistry,
        };
    }
    static buildFromSheets(sheets, configInput) {
        const config = new Config(configInput);
        return this.buildEngine(config, sheets);
    }
    static buildFromSheet(sheet, configInput) {
        const config = new Config(configInput);
        const newsheetprefix = config.translationPackage.getUITranslation(UIElement.NEW_SHEET_PREFIX) + '1';
        return this.buildEngine(config, { [newsheetprefix]: sheet });
    }
    static buildEmpty(configInput) {
        return this.buildEngine(new Config(configInput));
    }
    static rebuildWithConfig(config, sheets, stats) {
        return this.buildEngine(config, sheets, stats);
    }
}
